import { useState } from "react";
import { Formik, FieldArray } from "formik";
import styles from "./HavingQueryBuilder.module.css";
import SearchableDropdown from "../SearchableDropdown/SearchableDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faCode, faList, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../context/LanguageContext";
import {
  HavingCondition,
  AGGREGATE_FUNCTIONS,
  OPERATORS,
  LOGICAL_OPERATORS,
  parseHaving,
  conditionsToSQL,
  isValueRequired,
  extractFunction,
  extractFieldName,
  createFieldString,
} from "./havingQueryUtils";

interface HavingQueryBuilderProps {
  value: string;
  onChange: (value: string) => void;
  fields: Array<{ label: string; value: string }>;
}

export default function HavingQueryBuilder({
  value,
  onChange,
  fields,
}: HavingQueryBuilderProps) {
  const { t } = useLanguage();
  const [mode, setMode] = useState<"visual" | "manual">("visual");

  const initialConditions = parseHaving(value);

  const handleVisualChange = (conditions: HavingCondition[]) => {
    const sql = conditionsToSQL(conditions);
    onChange(sql);
  };

  const handleClear = () => {
    onChange("");
  };

  const renderValueInput = (
    cond: HavingCondition,
    index: number,
    setFieldValue: any,
    handleConditionsChange: () => void
  ) => {
    const operator = cond.operator;

    if (!isValueRequired(operator)) {
      return (
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>{t('value')}</label>
          <div className={styles.noValueNeeded}>
            {t('valueNotRequired')}
          </div>
        </div>
      );
    }

    if (operator === "BETWEEN") {
      const [val1, val2] = String(cond.value || "").split(" AND ");
      return (
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>{t('value')}</label>
          <div className={styles.betweenInputs}>
            <input
              type="text"
              className={styles.valueInput}
              value={val1 || ""}
              onChange={(e) => {
                const newValue = `${e.target.value} AND ${val2 || ""}`;
                setFieldValue(`conditions.${index}.value`, newValue);
                setTimeout(handleConditionsChange, 0);
              }}
              placeholder={t('from')}
            />
            <span className={styles.betweenSeparator}>—</span>
            <input
              type="text"
              className={styles.valueInput}
              value={val2 || ""}
              onChange={(e) => {
                const newValue = `${val1 || ""} AND ${e.target.value}`;
                setFieldValue(`conditions.${index}.value`, newValue);
                setTimeout(handleConditionsChange, 0);
              }}
              placeholder={t('to')}
            />
          </div>
        </div>
      );
    }

    if (operator === "IN" || operator === "NOT IN") {
      return (
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>{t('valuesCommaSeparated')}</label>
          <input
            type="text"
            className={styles.valueInput}
            value={cond.value}
            onChange={(e) => {
              setFieldValue(`conditions.${index}.value`, e.target.value);
              setTimeout(handleConditionsChange, 0);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConditionsChange();
              }
            }}
            placeholder={t('valuesPlaceholder')}
          />
        </div>
      );
    }

    return (
      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel}>{t('value')}</label>
        <input
          type="text"
          className={styles.valueInput}
          value={cond.value}
          onChange={(e) => {
            setFieldValue(`conditions.${index}.value`, e.target.value);
            setTimeout(handleConditionsChange, 0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleConditionsChange();
            }
          }}
          placeholder={t('enterValue')}
        />
      </div>
    );
  };

  const translatedOperators = OPERATORS.map(op => ({
    value: op.value,
    label: t(op.label)
  }));

  const translatedLogicalOperators = LOGICAL_OPERATORS.map(op => ({
    value: op.value,
    label: t(op.label)
  }));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <label className={styles.label}>{t('havingQuery')}</label>
        <div className={styles.headerActions}>
          {value && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={handleClear}
              title={t('clear')}
            >
              <FontAwesomeIcon icon={faTrash} />
              {t('clear')}
            </button>
          )}
          <div className={styles.modeToggle}>
            <button
              type="button"
              className={`${styles.modeBtn} ${
                mode === "visual" ? styles.active : ""
              }`}
              onClick={() => setMode("visual")}
            >
              <FontAwesomeIcon icon={faList} />
              {t('visual')}
            </button>
            <button
              type="button"
              className={`${styles.modeBtn} ${
                mode === "manual" ? styles.active : ""
              }`}
              onClick={() => setMode("manual")}
            >
              <FontAwesomeIcon icon={faCode} />
              SQL
            </button>
          </div>
        </div>
      </div>

      {mode === "manual" ? (
        <textarea
          className={styles.sqlInput}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              onChange(e.currentTarget.value);
            }
          }}
          placeholder={t('sqlPlaceholder')}
          rows={4}
        />
      ) : (
        <Formik
          initialValues={{ conditions: initialConditions }}
          enableReinitialize
          onSubmit={() => {}}
        >
          {({ values, setFieldValue }) => {
            const handleConditionsChange = () => {
              handleVisualChange(values.conditions);
            };

            return (
              <FieldArray name="conditions">
                {({ push, remove }) => (
                  <div className={styles.conditionsList}>
                    {values.conditions.length === 0 ? (
                      <div className={styles.emptyState}>
                        <p>{t('noConditions')}</p>
                      </div>
                    ) : (
                      values.conditions.map((cond, index) => (
                        <div key={index}>
                          <div className={styles.conditionRow}>
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              onClick={() => {
                                remove(index);
                                const newConditions = values.conditions.filter(
                                  (_, i) => i !== index
                                );
                                const newSQL = conditionsToSQL(newConditions);
                                onChange(newSQL);
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>

                            <div className={styles.fieldGroup}>
                              <label className={styles.fieldLabel}>
                                {t('aggregateFunction')}
                              </label>
                              <SearchableDropdown
                                label=""
                                options={AGGREGATE_FUNCTIONS}
                                value={extractFunction(cond.field)}
                                onChange={(v) => {
                                  const fieldName = extractFieldName(cond.field) || fields[0]?.value || "id";
                                  setFieldValue(
                                    `conditions.${index}.field`,
                                    createFieldString(v, fieldName)
                                  );
                                  setTimeout(handleConditionsChange, 0);
                                }}
                              />
                            </div>

                            <div className={styles.fieldGroup}>
                              <label className={styles.fieldLabel}>{t('field')}</label>
                              <SearchableDropdown
                                label=""
                                options={fields}
                                value={extractFieldName(cond.field) || fields[0]?.value || ""}
                                onChange={(v) => {
                                  const func = extractFunction(cond.field);
                                  setFieldValue(
                                    `conditions.${index}.field`,
                                    createFieldString(func, v)
                                  );
                                  setTimeout(handleConditionsChange, 0);
                                }}
                              />
                            </div>

                            <div className={styles.fieldGroup}>
                              <label className={styles.fieldLabel}>
                                {t('operator')}
                              </label>
                              <SearchableDropdown
                                label=""
                                options={translatedOperators}
                                value={cond.operator}
                                onChange={(v) => {
                                  setFieldValue(`conditions.${index}.operator`, v);
                                  if (!isValueRequired(v)) {
                                    setFieldValue(`conditions.${index}.value`, "");
                                  }
                                  setTimeout(handleConditionsChange, 0);
                                }}
                              />
                            </div>

                            {renderValueInput(
                              cond,
                              index,
                              setFieldValue,
                              handleConditionsChange
                            )}
                          </div>

                          {index < values.conditions.length - 1 && (
                            <div className={styles.logicalOperatorRow}>
                              <SearchableDropdown
                                label=""
                                options={translatedLogicalOperators}
                                value={cond.logicalOperator || "AND"}
                                onChange={(v) => {
                                  setFieldValue(
                                    `conditions.${index}.logicalOperator`,
                                    v
                                  );
                                  setTimeout(handleConditionsChange, 0);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))
                    )}

                    <button
                      type="button"
                      className={styles.addBtn}
                      onClick={() => {
                        push({
                          field: createFieldString("COUNT", fields[0]?.value || "id"),
                          operator: "=",
                          value: "",
                          logicalOperator: "AND",
                        });
                        setTimeout(handleConditionsChange, 0);
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      {t('addCondition')}
                    </button>
                  </div>
                )}
              </FieldArray>
            );
          }}
        </Formik>
      )}

      {mode === "visual" && value && (
        <div className={styles.sqlPreview}>
          <span className={styles.previewLabel}>SQL:</span>
          <code className={styles.previewCode}>{value}</code>
        </div>
      )}
    </div>
  );
}